import React from "react";
import {Button, Dropdown} from "antd";
import type {MenuProps} from "antd";
import {AppstoreOutlined, LogoutOutlined, PlusOutlined, TeamOutlined} from "@ant-design/icons";
import Logo from "./Logo";
import styles from "./TopBar.module.css";

export type View = "apps" | "team";

interface TopBarProps {
    username?: string
    isAdmin: boolean
    view: View
    onNavigate: (view: View) => void
    onUpload: () => void
    onLogout: () => void
}

export function initials(name = "") {
    const parts = name.split(/[.\s_-]+/).filter(Boolean);
    return (parts.length > 1 ? parts[0][0] + parts[1][0] : (parts[0] ?? "").slice(0, 2)).toUpperCase();
}

export default function TopBar({username, isAdmin, view, onNavigate, onUpload, onLogout}: TopBarProps) {
    const menu: MenuProps["items"] = [
        {
            key: "who",
            disabled: true,
            label: <span className={styles.who}>Signed in as<strong>{username}</strong></span>,
        },
        {type: "divider"},
        {key: "logout", icon: <LogoutOutlined/>, label: "Sign out", onClick: onLogout},
    ];

    return (
        <header className={styles.topBar}>
            <div className={styles.inner}>
                <div className={styles.brand}>
                    <Logo className={styles.logo}/>
                    <span className={styles.slash} aria-hidden>/</span>
                    {isAdmin ? (
                        <nav className={styles.nav} aria-label="Sections">
                            {([["apps", "Apps", <AppstoreOutlined key="i"/>], ["team", "Team", <TeamOutlined key="i"/>]] as const).map(([key, label, icon]) => (
                                <a
                                    key={key}
                                    href={`#${key}`}
                                    className={`${styles.navLink} ${view === key ? styles.navLinkActive : ""}`}
                                    aria-current={view === key ? "page" : undefined}
                                    onClick={event => {
                                        event.preventDefault();
                                        onNavigate(key);
                                    }}
                                >
                                    <span className={styles.navIcon} aria-hidden>{icon}</span>
                                    {label}
                                </a>
                            ))}
                        </nav>
                    ) : (
                        <span className={styles.crumb}>Apps</span>
                    )}
                </div>
                <div className={styles.actions}>
                    {isAdmin && view === "apps" && (
                        <Button type="primary" icon={<PlusOutlined/>} onClick={onUpload} aria-label="Upload build">
                            <span className={styles.uploadLabel}>Upload build</span>
                        </Button>
                    )}
                    <Dropdown menu={{items: menu}} trigger={["click"]} placement="bottomRight">
                        <button type="button" className={styles.avatar} aria-label={`Account menu for ${username}`}>
                            {initials(username)}
                        </button>
                    </Dropdown>
                </div>
            </div>
        </header>
    );
}
